/**
 * 系统设置 · AI 配置（AICFG-1）
 * 契约：designs/mockups/settings-ai/AI配置-样稿.html + API_MAP.md（tag aicfg-mockup-v1）
 *
 * 只对 owner / tech_admin 可见——前端隐藏入口（SettingsTabs 不显示 tab）+ 本页自己
 * 再挡一道（不发请求），照 RecycleBinPage.tsx 的写法，双保险。
 *
 * 三块：①连接卡 ②功能分配表 ③调用记录，均已接入。
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity, AlertTriangle, Bot, Check, Info, Lock, Pencil, Plug, Plus, Power, ShieldAlert, X, Zap,
} from "lucide-react";
import {
  createAiConnection, getAiConnectionModels, getAiUsage, listAiCalls, listAiConnections, listAiFeatures,
  patchAiConnection, patchAiFeature, testSavedAiConnection, testUnsavedAiConnection,
} from "@/services/aiSettings";
import type {
  AiCallRow, AiConnection, AiConnectionCreate, AiFeature, AiFeaturePatch, AiProvider,
} from "@/services/types/aiSettings";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { useCan } from "@/auth/permissions";
import { SettingsTabs } from "./SettingsTabs";

// ─────────────────────────────────────────────────────────────────────────
// 小组件（照现有设置页 / 渠道中心同款 Card·Pill·Field 写法，铁律十七）
// ─────────────────────────────────────────────────────────────────────────
function Card({ children }: { children: React.ReactNode }) {
  return <section className="rounded-[14px] border border-slate-200 bg-white p-5 shadow-sm">{children}</section>;
}

function SectionHead({ icon, title, sub, right }: { icon: React.ReactNode; title: string; sub?: string; right?: React.ReactNode }) {
  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600">{icon}</span>
      <h2 className="text-[15.5px] font-semibold text-slate-800">{title}</h2>
      {sub && <span className="text-[12px] text-slate-400">{sub}</span>}
      {right && <span className="ml-auto flex items-center gap-1.5">{right}</span>}
    </div>
  );
}

function Pill({ tone, icon, children }: { tone: "green" | "amber" | "gray" | "red"; icon?: React.ReactNode; children: React.ReactNode }) {
  const cls = {
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    gray: "bg-slate-100 text-slate-600",
    red: "bg-rose-50 text-rose-600",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      {icon}
      {children}
    </span>
  );
}

// 类型 → 预填接口地址（弹窗「类型」选择时自动填；「OpenAI 兼容」清空让人手填）
const PROVIDER_PRESETS: { value: AiProvider; label: string; baseUrl: string }[] = [
  { value: "openai", label: "OpenAI", baseUrl: "https://api.openai.com/v1" },
  { value: "zhipu", label: "智谱", baseUrl: "https://open.bigmodel.cn/api/paas/v4" },
  { value: "gemini", label: "Google Gemini", baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai" },
  { value: "deepseek", label: "DeepSeek", baseUrl: "https://api.deepseek.com/v1" },
  { value: "compatible", label: "OpenAI 兼容（自建 / 本地模型）", baseUrl: "" },
];
const PROVIDER_LABEL: Record<AiProvider, string> = Object.fromEntries(PROVIDER_PRESETS.map((p) => [p.value, p.label])) as Record<AiProvider, string>;

function fmtDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─────────────────────────────────────────────────────────────────────────
// ① 连接卡
// ─────────────────────────────────────────────────────────────────────────
function ConnectionLastTestPill({ conn }: { conn: AiConnection }) {
  if (!conn.last_test) return <Pill tone="gray"><span className="h-1.5 w-1.5 rounded-full bg-current" />未测试</Pill>;
  if (conn.last_test.ok) {
    return (
      <Pill tone="green" icon={<Check size={12} />}>
        测试通过{conn.last_test.latency_ms != null ? ` · ${conn.last_test.latency_ms}ms` : ""}
      </Pill>
    );
  }
  return (
    <Pill tone="red" icon={<AlertTriangle size={12} />}>
      测试失败 · {conn.last_test.error ?? "未知原因"}
    </Pill>
  );
}

function ConnectionRow({ conn, onEdit }: { conn: AiConnection; onEdit: () => void }) {
  const qc = useQueryClient();
  const testMut = useMutation({
    mutationFn: () => testSavedAiConnection(conn.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-settings", "connections"] }),
  });
  const toggleMut = useMutation({
    mutationFn: () => patchAiConnection(conn.id, { is_active: !conn.is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-settings", "connections"] }),
  });

  return (
    <div className={`grid grid-cols-1 items-center gap-3.5 rounded-[10px] border border-slate-200 p-3.5 md:grid-cols-[1.3fr_1.2fr_1.4fr_auto] ${conn.is_active ? "" : "bg-slate-50/60 opacity-75"}`}>
      <div>
        <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-900">
          {conn.name}
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${conn.provider === "compatible" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`}>
            {PROVIDER_LABEL[conn.provider] ?? conn.provider}
          </span>
        </div>
        <div className="mt-1 truncate font-mono text-[12px] text-slate-400">{conn.base_url}</div>
      </div>

      <div>
        <div className="text-[11.5px] text-slate-400">API Key</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[13px]">
          {conn.key_set ? (
            <>
              <Lock size={12} className="text-emerald-600" />
              <span className="whitespace-nowrap">已设置</span>
              {conn.key_updated_at && <span className="text-slate-400">· {fmtDateTime(conn.key_updated_at)}{conn.key_updated_by_name ? ` · ${conn.key_updated_by_name}` : ""}</span>}
            </>
          ) : (
            <>
              <span className="text-slate-300">○</span>
              <span className="text-slate-400">未设置（本地模型可不填）</span>
            </>
          )}
        </div>
      </div>

      <div>
        <div className="text-[11.5px] text-slate-400">连接状态</div>
        <div className="mt-0.5"><ConnectionLastTestPill conn={conn} /></div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {conn.is_active ? (
          <>
            <button
              type="button"
              disabled={testMut.isPending}
              onClick={() => testMut.mutate()}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50"
            >
              <Zap size={12} /> {testMut.isPending ? "测试中…" : "测试连接"}
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600"
            >
              <Pencil size={12} /> 编辑
            </button>
            <button
              type="button"
              disabled={toggleMut.isPending}
              onClick={() => toggleMut.mutate()}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:border-rose-300 hover:text-rose-600 disabled:opacity-50"
            >
              <Power size={12} /> 停用
            </button>
          </>
        ) : (
          <>
            <Pill tone="gray">已停用</Pill>
            <button
              type="button"
              disabled={toggleMut.isPending}
              onClick={() => toggleMut.mutate()}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50"
            >
              <Power size={12} /> 启用
            </button>
          </>
        )}
      </div>
      {toggleMut.isError && (
        <div className="md:col-span-4 text-[12px] text-rose-600">
          {toggleMut.error instanceof Error ? toggleMut.error.message : "操作失败，请重试"}
        </div>
      )}
    </div>
  );
}

interface ConnectionDraft {
  name: string;
  provider: AiProvider;
  base_url: string;
  api_key: string;
}

function ConnectionModal({ editing, onClose }: { editing: AiConnection | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ConnectionDraft>(() =>
    editing
      ? { name: editing.name, provider: editing.provider, base_url: editing.base_url, api_key: "" }
      : { name: "", provider: "openai", base_url: PROVIDER_PRESETS[0].baseUrl, api_key: "" },
  );
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const testMut = useMutation({
    mutationFn: () =>
      testUnsavedAiConnection({
        provider: draft.provider,
        base_url: draft.base_url,
        api_key: draft.api_key || undefined,
        connection_id: editing && !draft.api_key ? editing.id : undefined,
      }),
    onSuccess: (r) => {
      setTestResult({
        ok: r.ok,
        message: r.ok ? `连接成功 · ${r.latency_ms ?? "-"}ms · 读到 ${r.model_count ?? 0} 个模型` : (r.error ?? "测试失败"),
      });
    },
    onError: (e) => setTestResult({ ok: false, message: e instanceof Error ? e.message : "测试失败" }),
  });

  const saveMut = useMutation({
    mutationFn: () => {
      if (editing) {
        return patchAiConnection(editing.id, {
          name: draft.name,
          base_url: draft.base_url,
          api_key: draft.api_key || undefined,
        });
      }
      const body: AiConnectionCreate = { name: draft.name, provider: draft.provider, base_url: draft.base_url };
      if (draft.api_key) body.api_key = draft.api_key;
      return createAiConnection(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-settings", "connections"] });
      onClose();
    },
  });

  const onProviderChange = (p: AiProvider) => {
    const preset = PROVIDER_PRESETS.find((x) => x.value === p);
    setDraft((d) => ({ ...d, provider: p, base_url: preset?.baseUrl ?? d.base_url }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Plug size={18} className="text-blue-600" /> {editing ? "编辑连接" : "新增连接"}
          </h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="mt-4 flex flex-col gap-3.5">
          <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
            名称
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="如 OpenAI（公司）"
              className="rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-blue-500"
            />
          </label>

          <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
            类型
            <select
              value={draft.provider}
              onChange={(e) => onProviderChange(e.target.value as AiProvider)}
              disabled={!!editing}
              className="rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
            >
              {PROVIDER_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            {editing && <span className="text-[11.5px] text-slate-400">类型创建后不可改——要换服务商就新建连接</span>}
          </label>

          <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
            接口地址
            <input
              value={draft.base_url}
              onChange={(e) => setDraft({ ...draft, base_url: e.target.value })}
              className="rounded-lg border border-slate-200 px-3 py-2 font-mono text-[13px] outline-none focus:border-blue-500"
            />
            <span className="text-[11.5px] text-slate-400">选类型会自动填好；自建 / 本地模型手填</span>
          </label>

          <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
            API Key
            <input
              type="password"
              autoComplete="new-password"   // CREDAC-1 · 浏览器对 password 框常忽略 off：用 new-password 才不会把后台登录密码填进 API Key
              value={draft.api_key}
              onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
              placeholder={editing ? "已设置 · 留空 = 不修改" : "sk-…"}
              className="rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-blue-500"
            />
            <span className="text-[11.5px] text-slate-400">
              {editing ? "留空保持原 Key 不变；填入新值即替换。保存后不再显示。" : "保存后不再显示。本地模型可留空。"}
            </span>
          </label>

          {testResult && (
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-[12.5px] ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
              {testResult.ok ? <Check size={14} /> : <AlertTriangle size={14} />}
              {testResult.message}
            </div>
          )}
          {saveMut.isError && (
            <p className="text-[12.5px] text-rose-600">
              {saveMut.error instanceof Error ? saveMut.error.message : "保存失败，请重试"}
            </p>
          )}
        </div>

        <div className="mt-5 flex items-center gap-2 border-t border-slate-100 pt-4">
          <button
            type="button"
            disabled={testMut.isPending || !draft.base_url}
            onClick={() => testMut.mutate()}
            className="mr-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Zap size={13} /> {testMut.isPending ? "测试中…" : "先测试"}
          </button>
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">
            取消
          </button>
          <button
            type="button"
            disabled={!draft.name || !draft.base_url || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saveMut.isPending ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConnectionsCard() {
  const [modalMode, setModalMode] = useState<"closed" | "new" | AiConnection>("closed");
  const q = useQuery({ queryKey: ["ai-settings", "connections"], queryFn: listAiConnections });

  return (
    <Card>
      <SectionHead
        icon={<Plug size={15} />}
        title="AI 服务商连接"
        sub="一个连接 = 一家服务商的接口地址 + Key；下面的「功能分配」从这里选"
        right={
          <button
            type="button"
            onClick={() => setModalMode("new")}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700"
          >
            <Plus size={14} /> 新增连接
          </button>
        }
      />

      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState message="连接列表加载失败" onRetry={() => q.refetch()} />
      ) : !q.data?.length ? (
        <p className="py-6 text-center text-[13px] text-slate-400">还没有配置任何 AI 连接</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {q.data.map((conn) => (
            <ConnectionRow key={conn.id} conn={conn} onEdit={() => setModalMode(conn)} />
          ))}
        </div>
      )}

      <div className="mt-3.5 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
        <Info size={14} className="mt-0.5 shrink-0 text-slate-400" />
        <span>
          Key 只能填、不能看：保存后不再显示，连部分字符也不显示，要换就重新填。类型可选 OpenAI / 智谱 / Google Gemini /
          DeepSeek / OpenAI 兼容（自建、本地模型）。正被功能使用的连接不能停用，要先在下面改掉分配。
        </span>
      </div>

      {modalMode !== "closed" && (
        <ConnectionModal
          editing={modalMode === "new" ? null : modalMode}
          onClose={() => setModalMode("closed")}
        />
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ② 功能分配表
// ─────────────────────────────────────────────────────────────────────────
function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: (next: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        disabled ? "cursor-not-allowed opacity-40" : ""
      } ${checked ? "bg-emerald-500" : "bg-slate-300"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`}
      />
    </button>
  );
}

function FeatureRow({ feature, connections }: { feature: AiFeature; connections: AiConnection[] }) {
  const qc = useQueryClient();
  const [modelDraft, setModelDraft] = useState(feature.model ?? "");
  const [timeoutDraft, setTimeoutDraft] = useState(String(feature.timeout_s));
  const [rowError, setRowError] = useState<string | null>(null);

  const isPending = feature.status === "pending";
  const activeConnections = connections.filter((c) => c.is_active);

  const modelsQ = useQuery({
    queryKey: ["ai-settings", "models", feature.connection_id],
    queryFn: () => getAiConnectionModels(feature.connection_id as string),
    enabled: !!feature.connection_id,
  });

  const patchMut = useMutation({
    mutationFn: (body: AiFeaturePatch) => patchAiFeature(feature.feature, body),
    onSuccess: () => {
      setRowError(null);
      qc.invalidateQueries({ queryKey: ["ai-settings", "features"] });
    },
    onError: (e) => setRowError(e instanceof Error ? e.message : "保存失败，请重试"),
  });

  if (isPending) {
    return (
      <tr className="text-slate-400">
        <td className="px-2.5 py-2.5">
          <div className="font-semibold text-slate-500">{feature.label}</div>
          <div className="text-[11.5px]">{feature.description}</div>
        </td>
        <td className="px-2.5 py-2.5"><ToggleSwitch checked={false} onChange={() => {}} disabled /></td>
        <td className="px-2.5 py-2.5">
          <select disabled className="rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-[13px] text-slate-400"><option>—</option></select>
        </td>
        <td className="px-2.5 py-2.5"><input disabled value="" className="w-32 rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-[13px]" /></td>
        <td className="px-2.5 py-2.5"><input disabled value="" className="w-14 rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-[13px]" /> 秒</td>
        <td className="px-2.5 py-2.5"><Pill tone="amber">待接</Pill></td>
        <td className="px-2.5 py-2.5 text-[12px]">{feature.feature === "listing_agent" ? "LIST-3 上线后启用" : "未开发"}</td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        <td className="px-2.5 py-2.5">
          <div className="font-semibold text-slate-800">{feature.label}</div>
          <div className="text-[11.5px] text-slate-400">{feature.description}</div>
        </td>
        <td className="px-2.5 py-2.5">
          <ToggleSwitch checked={feature.enabled} onChange={(next) => patchMut.mutate({ enabled: next })} disabled={patchMut.isPending} />
        </td>
        <td className="px-2.5 py-2.5">
          <select
            value={feature.connection_id ?? ""}
            onChange={(e) => patchMut.mutate({ connection_id: e.target.value })}
            className="rounded-md border border-slate-300 px-2 py-1 text-[13px] outline-none focus:border-blue-500"
          >
            <option value="">未选择</option>
            {activeConnections.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </td>
        <td className="px-2.5 py-2.5">
          <input
            list={`models-${feature.feature}`}
            value={modelDraft}
            onChange={(e) => setModelDraft(e.target.value)}
            onBlur={() => { if (modelDraft !== (feature.model ?? "")) patchMut.mutate({ model: modelDraft }); }}
            className="w-36 rounded-md border border-slate-300 px-2 py-1 text-[13px] outline-none focus:border-blue-500"
          />
          <datalist id={`models-${feature.feature}`}>
            {(modelsQ.data ?? []).map((m) => <option key={m} value={m} />)}
          </datalist>
        </td>
        <td className="px-2.5 py-2.5 whitespace-nowrap">
          <input
            type="number" min={10} max={300}
            value={timeoutDraft}
            onChange={(e) => setTimeoutDraft(e.target.value)}
            onBlur={() => {
              const n = Number(timeoutDraft);
              if (Number.isFinite(n) && n !== feature.timeout_s) patchMut.mutate({ timeout_s: n });
            }}
            className="w-14 rounded-md border border-slate-300 px-2 py-1 text-[13px] outline-none focus:border-blue-500"
          /> 秒
        </td>
        <td className="px-2.5 py-2.5">
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${feature.source === "db" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
            {feature.source === "db" ? "页面配置" : "配置文件"}
          </span>
        </td>
        <td className="px-2.5 py-2.5 text-[12px]">
          {feature.last_call ? (
            feature.last_call.ok ? (
              <Pill tone="green" icon={<Check size={12} />}>
                {fmtDateTime(feature.last_call.at)} · {feature.last_call.latency_ms != null ? `${(feature.last_call.latency_ms / 1000).toFixed(1)}s` : "-"}
              </Pill>
            ) : (
              <Pill tone="red" icon={<AlertTriangle size={12} />}>{fmtDateTime(feature.last_call.at)} 失败</Pill>
            )
          ) : (
            <span className="text-slate-400">还没调用过</span>
          )}
        </td>
      </tr>
      {rowError && (
        <tr>
          <td colSpan={7} className="px-2.5 pb-2 text-[12px] text-rose-600">{rowError}</td>
        </tr>
      )}
    </>
  );
}

function FeatureAssignmentCard({ featuresQ, connections }: { featuresQ: ReturnType<typeof useQuery<AiFeature[]>>; connections: AiConnection[] }) {
  return (
    <Card>
      <SectionHead
        icon={<Bot size={15} />}
        title="功能分配"
        sub="每个 AI 功能单独选连接和模型；关掉 = 这个功能在本站不可用（入口也不显示）"
      />
      {featuresQ.isLoading ? (
        <LoadingState />
      ) : featuresQ.isError ? (
        <ErrorState message="功能分配加载失败" onRetry={() => featuresQ.refetch()} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
                <th className="px-2.5 py-2">功能</th>
                <th className="px-2.5 py-2">开关</th>
                <th className="px-2.5 py-2">使用连接</th>
                <th className="px-2.5 py-2">模型</th>
                <th className="px-2.5 py-2">超时</th>
                <th className="px-2.5 py-2">配置来源</th>
                <th className="px-2.5 py-2">最近调用</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(featuresQ.data ?? []).map((f) => (
                <FeatureRow key={f.feature} feature={f} connections={connections} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3.5 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
        <Info size={14} className="mt-0.5 shrink-0 text-slate-400" />
        <span>
          模型输入框的候选来自该连接「测试连接」时读到的模型列表，也可以手填。<b>配置来源</b>：「页面配置」= 以本页为准；
          「配置文件」= 本页还没保存过，沿用服务器配置文件里的设置（在本页保存一次后自动切成页面配置）。
          改动<b>保存后半分钟内生效</b>，不用重启。
        </span>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ③ 调用记录
// ─────────────────────────────────────────────────────────────────────────
function monthOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: `${d.getFullYear()} 年 ${d.getMonth() + 1} 月` });
  }
  return out;
}

/** 合计卡用「15.2 万」这种中文单位；逐条记录表用千分位（见下方 CallsTable）。 */
function fmtTokenZh(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)} 万`;
  return n.toLocaleString("en-US");
}

function featureDisplayLabel(feature: string, features: AiFeature[] | undefined): string {
  if (feature === "connection_test") return "连接测试";
  return features?.find((f) => f.feature === feature)?.label ?? feature;
}

function UsageCards({ featuresQ, month }: { featuresQ: ReturnType<typeof useQuery<AiFeature[]>>; month: string }) {
  const usageQ = useQuery({ queryKey: ["ai-settings", "usage", month], queryFn: () => getAiUsage(month) });
  const usageFor = (feature: string) => usageQ.data?.find((u) => u.feature === feature);

  return (
    <div className="mb-3.5 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
      {(featuresQ.data ?? []).map((f) => {
        const u = usageFor(f.feature);
        if (f.status === "pending" || !u) {
          return (
            <div key={f.feature} className="rounded-[10px] border border-slate-200 bg-slate-50/50 p-3">
              <p className="text-[12px] text-slate-500">{f.label} · 本月</p>
              <b className="text-[14px] font-semibold text-slate-400">待接</b>
            </div>
          );
        }
        return (
          <div key={f.feature} className="rounded-[10px] border border-slate-200 p-3">
            <p className="text-[12px] text-slate-600">{f.label} · 本月</p>
            <b className="text-[18px] font-semibold text-slate-900">{u.calls} 次</b>
            <p className="mt-0.5 text-[11.5px] text-slate-400">输入 {fmtTokenZh(u.input_tokens)} · 输出 {fmtTokenZh(u.output_tokens)} token</p>
          </div>
        );
      })}
    </div>
  );
}

function CallsTable({ rows, features }: { rows: AiCallRow[]; features: AiFeature[] | undefined }) {
  if (!rows.length) return <p className="py-6 text-center text-[13px] text-slate-400">近期没有调用记录</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
            <th className="px-2.5 py-2">时间</th>
            <th className="px-2.5 py-2">功能</th>
            <th className="px-2.5 py-2">连接 · 模型</th>
            <th className="px-2.5 py-2">关联对象</th>
            <th className="px-2.5 py-2">耗时</th>
            <th className="px-2.5 py-2">输入 / 输出 token</th>
            <th className="px-2.5 py-2">结果</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((c, i) => (
            <tr key={i}>
              <td className="px-2.5 py-2 font-mono text-[12px] text-slate-500">{fmtDateTime(c.at)}</td>
              <td className="px-2.5 py-2">{featureDisplayLabel(c.feature, features)}</td>
              <td className="px-2.5 py-2">{c.connection_name ?? "—"}{c.model ? ` · ${c.model}` : ""}</td>
              <td className="px-2.5 py-2 text-slate-600">{c.ref_label ?? "—"}</td>
              <td className="px-2.5 py-2">{c.latency_ms != null ? `${(c.latency_ms / 1000).toFixed(1)}s` : "—"}</td>
              <td className="px-2.5 py-2 font-mono text-[12px]">
                {c.input_tokens != null ? c.input_tokens.toLocaleString("en-US") : "—"} / {c.output_tokens != null ? c.output_tokens.toLocaleString("en-US") : "—"}
              </td>
              <td className="px-2.5 py-2">
                {c.ok ? (
                  <Pill tone="green" icon={<Check size={12} />}>成功</Pill>
                ) : (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <Pill tone="red" icon={<AlertTriangle size={12} />}>失败</Pill>
                    <span className="text-[11.5px] text-rose-600">{c.error}</span>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageAndCallsCard({ featuresQ }: { featuresQ: ReturnType<typeof useQuery<AiFeature[]>> }) {
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].value);
  const [featureFilter, setFeatureFilter] = useState("");
  const [resultFilter, setResultFilter] = useState<"" | "ok" | "fail">("");

  const callsQ = useQuery({
    queryKey: ["ai-settings", "calls", featureFilter, resultFilter],
    queryFn: () => listAiCalls({ feature: featureFilter || undefined, result: resultFilter || undefined, limit: 50 }),
  });
  const availableFeatures = (featuresQ.data ?? []).filter((f) => f.status === "available");

  return (
    <Card>
      <SectionHead
        icon={<Activity size={15} />}
        title="调用记录"
        sub="只统计 token 数量，不换算金额（各家价格常变）"
        right={
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px]">
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        }
      />

      <UsageCards featuresQ={featuresQ} month={month} />

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select value={featureFilter} onChange={(e) => setFeatureFilter(e.target.value)} className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px]">
          <option value="">全部功能</option>
          {availableFeatures.map((f) => <option key={f.feature} value={f.feature}>{f.label}</option>)}
        </select>
        <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value as "" | "ok" | "fail")} className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px]">
          <option value="">全部结果</option>
          <option value="fail">只看失败</option>
        </select>
        <span className="text-[12px] text-slate-400">近 50 条</span>
      </div>

      {callsQ.isLoading ? (
        <LoadingState />
      ) : callsQ.isError ? (
        <ErrorState message="调用记录加载失败" onRetry={() => callsQ.refetch()} />
      ) : (
        <CallsTable rows={callsQ.data ?? []} features={featuresQ.data} />
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 页面骨架
// ─────────────────────────────────────────────────────────────────────────
export function AiSettingsPage() {
  const canAccess = useCan("system.ai.view");

  if (!canAccess) {
    return (
      <PageShell title="AI 配置" subtitle="老板 / 技术管理员专属">
        <SettingsTabs />
        <div className="flex flex-col items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/60 px-6 py-16 text-center">
          <ShieldAlert className="h-7 w-7 text-amber-500" />
          <p className="text-[14px] font-semibold text-amber-800">仅老板 / 技术管理员可访问 AI 配置</p>
          <p className="text-[12px] text-amber-700">服务商连接和调用记录不是普通运营角色能看的东西</p>
        </div>
      </PageShell>
    );
  }

  return <AiSettingsContent />;
}

/** 拆出来是因为 ②③ 需要共享 ①③ 都在用的 features/connections 查询——react-query
    按 queryKey 去重，这里再声明一次不会多打一次网络请求，只是拿到同一份缓存数据。 */
function AiSettingsContent() {
  const featuresQ = useQuery({ queryKey: ["ai-settings", "features"], queryFn: listAiFeatures });
  const connectionsQ = useQuery({ queryKey: ["ai-settings", "connections"], queryFn: listAiConnections });

  return (
    <PageShell title="系统设置" subtitle="AI 配置仅老板 / 技术管理员可见；本站的配置只作用于本站（公司站、个人站各配各的）">
      <SettingsTabs />
      <div className="mt-4 flex flex-col gap-4">
        <ConnectionsCard />
        <FeatureAssignmentCard featuresQ={featuresQ} connections={connectionsQ.data ?? []} />
        <UsageAndCallsCard featuresQ={featuresQ} />
      </div>
    </PageShell>
  );
}
